import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  getAllCasinoStudiosStart,
  updateCasinoStudioStatusStart
} from '../../../store/redux-slices/superAdminCasinoManagement'
import useDidMountEffect from '../../../utils/useDidMountEffect'
import useAggregatorList from '../../../shared/hooks/useAggregatorList'
import { getTenantId } from '../../../utils/constants'

/**
 * Custom hook for Casino Studios listing
 * @param {boolean} isTenant - Whether the current user is a tenant
 * @returns {Object} - Hook state and functions
 */
const useCasinoStudiosListing = (isTenant = true) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isInitialRender = useDidMountEffect()

  // Common state for both tenant and super admin
  const [limit, setLimit] = useState(15)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusShow, setStatusShow] = useState(false)
  const [studioId, setStudioId] = useState()
  const [status, setStatus] = useState()
  const [name, setName] = useState()
  const [selectedProvider, setSelectedProvider] = useState('')

  // Super admin specific state
  const [selectedClient, setSelectedClient] = useState('')
  const [selectedPortal, setSelectedPortal] = useState('')

  const { studioLoading, casinoStudios } = useSelector((state) => state.superAdminCasino)

  const {
    aggregatorsOptions,
    selectedAggregatorId,
    setSelectedAggregatorId
  } = useAggregatorList({
    tenantId: isTenant ? getTenantId() : selectedPortal
  })

  const totalPages = Math.ceil(casinoStudios?.count / limit)

  const handleStatusShow = (id, status, myName) => {
    setStudioId(id)
    setStatus(!status)
    setName(myName)
    setStatusShow(true)
  }

  const handleYes = () => {
    const params = {
      casinoStudioId: studioId,
      isActive: status,
      isTenant,
      pageNo: page,
      limit,
      search,
      casinoProviderId: selectedProvider
    }

    // Add super admin specific parameters
    if (!isTenant) {
      params.tenantId = selectedPortal
      params.portalId = selectedClient
    }

    dispatch(updateCasinoStudioStatusStart(params))
    setStatusShow(false)
  }

  useEffect(() => {
    // For tenant, fetch provider data and studios data immediately
    if (isTenant) {
      // Fetch studios data
      dispatch(
        getAllCasinoStudiosStart({
          isTenant,
          pageNo: page,
          limit,
          search: '',
          casinoProviderId: '',
          masterGameAggregatorId: ''
        })
      )
    }
    // For superadmin, studios data will be fetched when portal is selected
    // Provider data will be fetched when both portal and tenant are selected
  }, [])

  // Handle search input changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (!isInitialRender) {
        if (page === 1) {
          // For super admin, only fetch if portal is selected
          if (isTenant || (!isTenant && selectedPortal)) {
            const params = {
              isTenant,
              pageNo: page,
              limit,
              search,
              casinoProviderId: selectedProvider,
              masterGameAggregatorId: selectedAggregatorId
            }

            // Add super admin specific parameters
            if (!isTenant) {
              params.tenantId = selectedPortal
              params.portalId = selectedClient
            }

            dispatch(getAllCasinoStudiosStart(params))
          }
        } else {
          setPage(1)
        }
      }
    }, 1000)

    return () => clearTimeout(delayDebounceFn)
  }, [search])

  // Handle filter changes
  useEffect(() => {
    setPage(1)

    // For super admin, only fetch if portal is selected
    if (isTenant || (!isTenant && selectedPortal)) {
      const params = {
        isTenant,
        pageNo: page,
        limit,
        search,
        casinoProviderId: selectedProvider,
        masterGameAggregatorId: selectedAggregatorId
      }

      // Add super admin specific parameters
      if (!isTenant) {
        params.tenantId = selectedPortal
        params.portalId = selectedClient
      }

      dispatch(getAllCasinoStudiosStart(params))
    }
  }, [limit, selectedProvider, selectedAggregatorId, ...(isTenant ? [] : [selectedPortal, selectedClient])])

  // Handle page changes
  useEffect(() => {
    // For super admin, only fetch if portal is selected and not initial render
    // For tenant, fetch if not initial render
    if (!isInitialRender && (isTenant || (!isTenant && selectedPortal))) {
      const params = {
        isTenant,
        pageNo: page,
        limit,
        search,
        casinoProviderId: selectedProvider,
        masterGameAggregatorId: selectedAggregatorId
      }

      // Add super admin specific parameters
      if (!isTenant) {
        params.tenantId = selectedPortal
        params.portalId = selectedClient
      }

      dispatch(getAllCasinoStudiosStart(params))
    }
  }, [page])

  // Return common properties for both tenant and super admin
  const commonProps = {
    limit,
    page,
    statusShow,
    setLimit,
    setPage,
    setStatusShow,
    casinoStudios,
    totalPages,
    handleStatusShow,
    handleYes,
    loading: studioLoading,
    search,
    setSearch,
    status,
    navigate,
    selectedProvider,
    setSelectedProvider,
    name,
    aggregatorsOptions,
    selectedAggregatorId,
    setSelectedAggregatorId
  }

  // Add super admin specific properties
  if (!isTenant) {
    return {
      ...commonProps,
      selectedClient,
      setSelectedClient,
      selectedPortal,
      setSelectedPortal
    }
  }

  return commonProps
}

export default useCasinoStudiosListing
